/**
 * Employees Page
 *
 * HR and Admin view for managing employee profiles.
 * Shows employee list with search, filter, and CRUD actions.
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { CustomSelect } from '../../components/CustomSelect';
import { useEmployees, useNiveaus, useTeams } from './useEmployees';
import { formatDutchDate } from '../../lib/dateUtils';
import type { EmployeeFilters } from '../../lib/employeesApi';

export function EmployeesPage() {
  const [search, setSearch] = useState('');
  const [niveauFilter, setNiveauFilter] = useState<number | undefined>();
  const [teamFilter, setTeamFilter] = useState<string | undefined>();
  const [showInactive, setShowInactive] = useState(false);

  // Debounce search input
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useMemo(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Build filters object
  const filters: EmployeeFilters = useMemo(() => ({
    search: debouncedSearch || undefined,
    niveauId: niveauFilter,
    team: teamFilter,
    includeInactive: showInactive || undefined,
  }), [debouncedSearch, niveauFilter, teamFilter, showInactive]);

  // Fetch data
  const { data: employeesData, isLoading, error } = useEmployees(filters);
  const { data: niveaus } = useNiveaus();
  const { data: teams } = useTeams();

  return (
    <Layout>
      <div data-testid="employees-page">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Medewerkers</h1>
            <p className="mt-1 text-sm text-gray-500">
              Beheer medewerkerprofielen en functieniveaus
            </p>
          </div>
          <Link
            to="/medewerkers/nieuw"
            className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="mr-2 -ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Nieuwe Medewerker
          </Link>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Zoeken
              </label>
              <div className="input-icon-wrapper">
                <svg className="input-icon h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  id="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Zoek op naam of email..."
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Niveau Filter */}
            <div>
              <label htmlFor="niveau" className="block text-sm font-medium text-gray-700 mb-1">
                Niveau
              </label>
              <CustomSelect
                id="niveau"
                value={niveauFilter?.toString() ?? ''}
                onChange={(val) => setNiveauFilter(val ? Number(val) : undefined)}
                options={[
                  { value: '', label: 'Alle niveaus' },
                  ...(niveaus?.map((niveau) => ({
                    value: niveau.id.toString(),
                    label: niveau.name,
                  })) ?? []),
                ]}
                className="block w-full"
              />
            </div>

            {/* Team Filter */}
            <div>
              <label htmlFor="team" className="block text-sm font-medium text-gray-700 mb-1">
                Team
              </label>
              <CustomSelect
                id="team"
                value={teamFilter ?? ''}
                onChange={(val) => setTeamFilter(val || undefined)}
                options={[
                  { value: '', label: 'Alle teams' },
                  ...(teams?.map((team) => ({
                    value: team,
                    label: team,
                  })) ?? []),
                ]}
                className="block w-full"
              />
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-700">
                Fout bij het laden van medewerkers. Probeer het opnieuw.
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Laden...</span>
          </div>
        )}

        {/* Employee List */}
        {!isLoading && employeesData && (
          <>
            {/* Results Count and Inactive Toggle */}
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {employeesData.total} {employeesData.total === 1 ? 'medewerker' : 'medewerkers'} gevonden
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="toon-uit-dienst-filter"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="rounded border-gray-300 text-[#54949E] focus:ring-[#54949E]"
                />
                Toon uit dienst
              </label>
            </div>

            {/* Table */}
            {employeesData.data.length > 0 ? (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Medewerker
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Niveau
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Functie
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Team
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Taken
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acties
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {employeesData.data.map((employee) => (
                      <tr key={employee.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                              {employee.name}
                              {employee.uitDienstDatum ? (
                                <span
                                  data-testid={`uit-dienst-status-chip-${employee.id}`}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
                                >
                                  Uit dienst per {formatDutchDate(employee.uitDienstDatum)}
                                </span>
                              ) : (
                                !employee.active && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                    Inactief
                                  </span>
                                )
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {employee.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {employee.niveau.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {employee.functie || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {employee.team || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {employee.tasksCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/medewerkers/${employee.id}`}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Bekijken
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-12 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Geen medewerkers gevonden
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  {search || niveauFilter || teamFilter
                    ? 'Probeer andere zoek- of filteropties.'
                    : 'Begin met het toevoegen van medewerkerprofielen.'}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
